import { defineStore } from 'pinia';
import api from '@/common/utils/api';
import sortStoragePoliciesByName from '@/common/utils/sorters/policySorters';
import { DOMAIN_QUOTA_SCOPE, PROJECT_QUOTA_SCOPE, QUOTA_SCOPE } from '@/common/constants/domainProjectsQuotas.constants';
import { set } from 'vue';
import { DomainQuotas, Quotas } from '@schema';

type UpdateQuotasRequestBody = {
  domainProjectId: ID,
  body: FetchRequestBody,
  unscopedUser: boolean
};

type Quota = {
  limit: number,
  used: number
}
type DomainProjectQuotaScopeMap<T> = Record<ID, T>;

type DomainProjectQuotaState = {
  [DOMAIN_QUOTA_SCOPE]: DomainProjectQuotaScopeMap<DomainQuotas>,
  [PROJECT_QUOTA_SCOPE]: DomainProjectQuotaScopeMap<Quotas>
};

type StoragePolicyQuotaMap = Record<string, Quota>; // string === name
type StoragePolicyQuotaState = {
  [DOMAIN_QUOTA_SCOPE]: DomainProjectQuotaScopeMap<StoragePolicyQuotaMap>,
  [PROJECT_QUOTA_SCOPE]: DomainProjectQuotaScopeMap<StoragePolicyQuotaMap>
};

const apiUrl = (quotaScope: QUOTA_SCOPE, id: ID, withUsage: boolean): string => quotaScope === DOMAIN_QUOTA_SCOPE
    ? `/api/v2/compute/domain-quotas/${id}?usage=${withUsage.toString()}`
    : `/api/v2/compute/quotas/${id}?usage=${withUsage.toString()}`;

export const useDomainProjectQuotasStore = defineStore('domainProjectQuotas', {
  state: () => ({
    loading: false,
    quotas: {
      [DOMAIN_QUOTA_SCOPE]: {},
      [PROJECT_QUOTA_SCOPE]: {}
    } as DomainProjectQuotaState,
    storagePolicies: {
      [DOMAIN_QUOTA_SCOPE]: {},
      [PROJECT_QUOTA_SCOPE]: {}
    } as StoragePolicyQuotaState
  }),
  getters: {
    getQuotasById: (state) => (quotaScope: QUOTA_SCOPE, id: ID) => state.quotas[quotaScope][id],
    getProjectQuotasById: (state) => (id: ID) => state.quotas[PROJECT_QUOTA_SCOPE][id],
    getStoragePoliciesSortedByName: (state) => (quotaScope, id) => state.storagePolicies[quotaScope][id],
    getProjectK8sLimit: (state) => (projectId) => {
      const quotas = state.quotas[PROJECT_QUOTA_SCOPE][projectId];
      return quotas?.k8saas?.cluster?.limit;
    },
    getProjectLbLimit: (state) => (projectId) => {
      const quotas = state.quotas[PROJECT_QUOTA_SCOPE][projectId];
      return quotas?.lbaas?.loadbalancer?.limit;
    },
    getProjectVPNLimit: (state) => (projectId) => {
      const quotas = state.quotas[PROJECT_QUOTA_SCOPE][projectId];
      return quotas?.network?.ipsec_site_connection?.limit;
    },
    getProjectStoragePolicyQuotas: (state) => (id, policyName) => state.storagePolicies[PROJECT_QUOTA_SCOPE][id] ? state.storagePolicies[PROJECT_QUOTA_SCOPE][id][policyName] : null,
    getAssignedPlacements: (state) => (projectId) => {
      const quotas = state.quotas[PROJECT_QUOTA_SCOPE][projectId];
      return quotas?.placement
        ? Object.entries(quotas.placement).reduce<string[]>((result, [key, val]) => val.limit ? [...result, key] : result, [])
        : [];
    }
  },
  actions: {
    async fetch(quotaScope: QUOTA_SCOPE, id: ID) {
      try {
        this.loading = true;
        const { data } = await api.get(apiUrl(quotaScope, id, true));
        set(this.quotas[quotaScope], id, data);
        set(this.storagePolicies[quotaScope], id, Object.keys(data.storage?.storage_policies)
            .sort(sortStoragePoliciesByName)
            .reduce((result, key) => {
              result[key] = data.storage?.storage_policies[key];
              return result;
            }, {}));
        return data;
      } finally {
        this.loading = false;
      }
    },
    fetchProjectQuotas(id: ID) {
      return this.fetch(PROJECT_QUOTA_SCOPE, id);
    },
    updateQuotas(quotaScope: QUOTA_SCOPE, { domainProjectId, body, unscopedUser }: UpdateQuotasRequestBody) {
      const options = unscopedUser
          ? { headers: { [api.scopedTokenHeaderName]: domainProjectId }, unscopedRequest: true }
          : {};

      return api.post(apiUrl(quotaScope, domainProjectId, false), body, options)
          .then(() => this.fetch(quotaScope, domainProjectId));
    }
  }
});

export default useDomainProjectQuotasStore;
